/**
 * useSubComponentRegistry - Hook for making internal elements of complex components selectable
 *
 * This hook allows complex components (like FermentationTankCard) to expose their internal
 * elements (buttons, labels, gauges) as individually selectable components in the editor.
 *
 * Sub-components:
 * - Are registered in the editor store with unique IDs (parentId::key)
 * - Appear nested under their parent in the component tree
 * - Can be selected and configured via AI chat commands
 * - Are automatically cleaned up when the parent unmounts
 *
 * Usage:
 * ```tsx
 * const { getSubComponentId, getSubComponent } = useSubComponentRegistry(component, [
 *   { key: 'view-details', type: ComponentType.BUTTON, name: 'View Details', properties: {...} },
 *   { key: 'tank-label', type: ComponentType.TEXT, name: 'Tank Label', properties: {...} },
 * ]);
 * ```
 */

import { useEffect, useCallback, useRef } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useShallow } from 'zustand/react/shallow';
import { Component, ComponentType } from '@/types';
import { useInteractionContext } from '@/components/shared/rendering/InteractionContextRuntime';

export interface SubComponentDefinition {
  /** Unique key within parent (e.g., 'view-details', 'tank-label') */
  key: string;
  /** Component type for property panel and AI commands */
  type: ComponentType | string;
  /** Semantic sub-component type: 'button', 'label', 'gauge', 'alert', etc. */
  subComponentType: string;
  /** Display name in component tree */
  name: string;
  /** Component properties */
  properties: Record<string, unknown>;
  /** Optional styles */
  style?: Record<string, unknown>;
  /** Optional size */
  size?: { width: number; height: number };
}

export interface SubComponentRegistryResult {
  /** Generate stable sub-component ID from key */
  getSubComponentId: (key: string) => string;
  /** Get current sub-component state from store (may have been modified by AI chat) */
  getSubComponent: (key: string) => Component | undefined;
  /** Check if sub-components are registered */
  isRegistered: boolean;
}

export function useSubComponentRegistry(
  parentComponent: Component,
  definitions: SubComponentDefinition[]
): SubComponentRegistryResult {
  // Check interaction mode - register sub-components in editing OR viewing modes
  // Both modes allow component selection. Only skip in functional mode (viewer)
  const { mode } = useInteractionContext();
  const canRegisterSubComponents = mode === 'editing' || mode === 'viewing';

  const addComponent = useEditorStore((state) => state.addComponent);
  const deleteComponent = useEditorStore((state) => state.deleteComponent);
  const getComponent = useEditorStore((state) => state.getComponent);
  const updateComponent = useEditorStore((state) => state.updateComponent);

  // Get project/page context from store as fallback
  const { project, currentPage } = useEditorStore(
    useShallow((state) => ({
      project: state.project,
      currentPage: state.currentPage,
    }))
  );

  // Track registered IDs for cleanup
  const registeredIdsRef = useRef<Set<string>>(new Set());
  const isRegisteredRef = useRef(false);

  // Generate stable sub-component ID
  const getSubComponentId = useCallback(
    (key: string) => {
      return `${parentComponent.id}::${key}`;
    },
    [parentComponent.id]
  );

  // Register sub-components on mount, cleanup on unmount
  useEffect(() => {
    // Skip registration in functional mode (viewer - no selection available)
    // Allow registration in editing and viewing modes (both support selection)
    if (!canRegisterSubComponents) {
      return;
    }

    // Get effective project/page IDs - prefer component values, fall back to store values
    const effectiveProjectId = parentComponent.projectId || project?.id;
    const effectivePageId = parentComponent.pageId || currentPage?.id;

    // Only register sub-components when we have valid project context
    const canRegister = effectiveProjectId && effectivePageId;

    if (!canRegister) {
      // If we can't register but have previously registered IDs, clean them up
      if (registeredIdsRef.current.size > 0) {
        registeredIdsRef.current.forEach((id) => {
          deleteComponent(id);
        });
        registeredIdsRef.current.clear();
        isRegisteredRef.current = false;
      }
      return;
    }

    const newIds = new Set<string>();

    definitions.forEach((def, index) => {
      const subId = getSubComponentId(def.key);
      const existing = getComponent(subId);

      // Create sub-component object
      const subComponent: Partial<Component> = {
        id: subId,
        projectId: effectiveProjectId,
        pageId: effectivePageId,
        name: def.name,
        type: def.type as ComponentType,
        properties: def.properties,
        style: def.style || {},
        size: def.size || { width: 0, height: 0 },
        position: { x: 0, y: 0, z: 1 },
        parentId: parentComponent.id,
        children: [],
        order: index,
        locked: false,
        hidden: false,
        isSubComponent: true,
        subComponentType: def.subComponentType,
        // Add required fields with defaults
        responsivePosition: {
          strategy: 'flex',
        },
        version: 1,
        createdBy: 'system',
        lastModifiedBy: 'system',
        lastModifiedAt: new Date(),
        isDeleted: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      if (!existing) {
        addComponent(subComponent as Component);
      } else {
        // Update existing to sync properties from parent
        updateComponent(subId, {
          properties: def.properties,
          name: def.name,
        });
      }

      newIds.add(subId);
      registeredIdsRef.current.add(subId);
    });

    isRegisteredRef.current = true;

    // Cleanup: remove sub-components that are no longer in definitions
    registeredIdsRef.current.forEach((id) => {
      if (!newIds.has(id)) {
        deleteComponent(id);
        registeredIdsRef.current.delete(id);
      }
    });

    // Copy ref values for cleanup functions (React best practice)
    const idsForUnmountCleanup = new Set(registeredIdsRef.current);
    const registeredIdsForRefCleanup = registeredIdsRef.current;

    // Cleanup on unmount
    return () => {
      idsForUnmountCleanup.forEach((id) => {
        deleteComponent(id);
      });
      registeredIdsForRefCleanup.clear();
      isRegisteredRef.current = false;
    };
  }, [
    canRegisterSubComponents,
    parentComponent.id,
    parentComponent.projectId,
    parentComponent.pageId,
    project?.id,
    currentPage?.id,
    definitions,
    getSubComponentId,
    addComponent,
    deleteComponent,
    getComponent,
    updateComponent,
  ]);

  // Get current sub-component state (may be modified by AI chat)
  const getSubComponent = useCallback(
    (key: string): Component | undefined => {
      return getComponent(getSubComponentId(key));
    },
    [getComponent, getSubComponentId]
  );

  return {
    getSubComponentId,
    getSubComponent,
    isRegistered: isRegisteredRef.current,
  };
}

export default useSubComponentRegistry;
